import axios from "axios";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { marked } from "marked";
import React, { useContext, useEffect, useState } from "react";
import { Breadcrumb, Dropdown, Menu, Button as AntdButton } from "antd";
import { DownOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Col,
  Container,
  Row,
  Table,
  UncontrolledAlert,
  Alert,
  Button,
} from "reactstrap";
import { getRoutesData, saveClientLogs } from "../../data/data.facade";
import "./charts.css";
import AccuracyChartDirector from "./services/director/accuracy.chart.director";
import ChangeChartDirector from "./services/director/change.chart.director";
import GaugeChartDirector from "./services/director/gauge.chart.director";
import HistoryChartDirector from "./services/director/history.chart.director";
import DistributionChartDirector from "./services/director/distribution.chart.director";
import TopTenChartDirector from "./services/director/topten.chart.director";
import { getTopTenDataForLead } from "./services/topten-data.service";
import { AccessContext } from "../../contexts/access";
import { RoutesContext } from "../../contexts/routes";
import { getParentsName } from "./services/breadcrumb.service";
import { SelectedFeatureContext } from "../../contexts/selectedFeature";
import { findChildren } from "./services/dropdown.service";
import { SelectedPeriodContext } from "../../contexts/selectedPeriod";
import Guide from "./guide";
import { NADataPage } from "./notFound";
import { findParent, findParentName, findUniqueId } from "./services/search.service";
import { noise } from "./services/data.service";
import { ChartsDataContext } from "../../contexts/chartsData";
import APIManager from "../../data/api/api.service";
require("highcharts/modules/exporting")(Highcharts);
require("highcharts/modules/export-data")(Highcharts);
require("highcharts/highcharts-more.js")(Highcharts);
require("highcharts/modules/solid-gauge")(Highcharts);

const TopTenChart = ({ topten, showHome, size }) => {
  return (
    <Col className="position-relative mb-4" xs={12} lg={size}>
      {topten && !showHome ? (
        <div className="chart-wrapper full-height">
          <HighchartsReact options={topten} highcharts={Highcharts} />
        </div>
      ) : (
        <></>
      )}
    </Col>
  );
};

const TopTenCharts = ({ charts }) => {
  if (!charts.length) return null;

  const leadSlots = [1, 2, 3];

  return (
    <Col xs={12} md={12}>
      <Row>
        {leadSlots.map((lead) => {
          const topTenChart = charts.find((item) => item.lead === lead);
          return topTenChart ? (
            <TopTenChart
              key={`mef-lead-${lead}`}
              size={4}
              topten={topTenChart.chart}
            />
          ) : (
            <Col className="position-relative mb-4" key={`mef-lead-${lead}-empty`} xs={12} lg={4} />
          );
        })}
      </Row>
    </Col>
  );
};

const TopTenChartDescription = ({ toptenDesc }) => {
  const convertedContent = toptenDesc?.content
    ? marked.parse(toptenDesc.content)
    : "";
  return (
    <Col className="position-relative mb-4" md={12}>
      <h2 style={{ color: 'rgb(51, 51, 51)', fontSize: '30px', textAlign: 'center', fontFamily: '"Lucida Grande", "Lucida Sans Unicode", Arial, Helvetica, sans-serif', marginTop: '30px', marginBottom: '20px' }}>Strategic Intelligence Brief</h2>
      <div className="bg-white" style={{ padding: '30px 40px' }}>
        <h2 style={{ color: 'rgb(51, 51, 51)', fontSize: '22px', textAlign: 'center', fontFamily: '"Lucida Grande", "Lucida Sans Unicode", Arial, Helvetica, sans-serif', marginBottom: '20px' }}>{toptenDesc?.title}</h2>
        <div dangerouslySetInnerHTML={{ __html: convertedContent }} />
      </div>
    </Col>
  );
};

const Gauge = ({ gauge, showHome, size }) => {
  return (
    <Col className="position-relative mb-4" xs={12} lg={size}>
      {gauge && !showHome ? (
        <div className="chart-wrapper full-height">
          <HighchartsReact options={gauge} highcharts={Highcharts} />
        </div>
      ) : (
        <></>
      )}
    </Col>
  );
};
const Periods = ({ routes, onChange }) => {
  function onClick(e) {
    onChange(String(e.key));
  }

  return (
    <Menu
      onClick={onClick}
      items={routes.map((i) => ({ label: i.name, key: i.id }))}
    />
  );
};

const PercentageChanges = ({ percentageChanges, showHome, size }) => {
  return (
    <Col className="position-relative mb-4" xs={12} lg={size}>
      {percentageChanges && !showHome ? (
        <div className="chart-wrapper full-height">
          <HighchartsReact
            options={percentageChanges}
            highcharts={Highcharts}
          />
        </div>
      ) : (
        <></>
      )}
    </Col>
  );
};

const DataStates = {
  Available: 0,
  Unavailable: 1,
  Unselect: 2,
};
const RouteHeader = ({
  setAlerts = () => { },
  alerts = [],
  siderController = () => { },
}) => {
  const { value: routes } = useContext(RoutesContext);
  const { value: selectedFeature } = useContext(SelectedFeatureContext);
  const { value: selectedPeriod, dispatch: dispatchSelectedPeriod } =
    useContext(SelectedPeriodContext);
  const { value: access } = useContext(AccessContext);
  const isDesktop = window.innerWidth > 768;
  const [routesChildren, setRoutesChildren] = useState([]);
  const [isDefault, setIsDefault] = useState(true);
  const [showAll, setShowAll] = useState(true);
  const [firstPeriod, setFirstPeriod] = useState("Loading...");

  function onChangeAlert() {
    if (showAll) setAlerts(alerts);
    else setAlerts(alerts.filter((i) => i.color !== "info"));
    setShowAll(!showAll);
  }

  useEffect(() => {
    const firstChild = routesChildren[0];
    const str = selectedFeature + "-" + selectedPeriod;
    const isDefaultPage = String(localStorage.defaultFeature) === String(str);
    if (isDefaultPage) {
      return;
    }
    setFirstPeriod(firstChild?.name || "Loading...");

    setShowAll(true);
    setAlerts([])
    if (firstChild !== undefined) changePeriod(firstChild?.id);
  }, [routesChildren]);
  useEffect(() => {
    const children = findChildren(routes, selectedFeature);
    const filtered = children.filter(c => access.includes(String(c.id)) || access.includes(Number(c.id)));
    const PeriodEnum = {
      weekly: 1,
      monthly: 2,
      quarterly: 3,
      annually: 4,
      annual: 5,
    };
    const SortedRoutes = filtered.sort((a, b) => {
      return (
        PeriodEnum[a.period.trim().toLowerCase()] -
        PeriodEnum[b.period.trim().toLowerCase()]
      );
    });

    setRoutesChildren(SortedRoutes);
    if (isFeature() && !isDesktop) {
      siderController(false);
    }
    setShowAll(true);
    setAlerts([])
  }, [selectedFeature]);

  function onSetDefault(e) {
    const str = selectedFeature + "-" + selectedPeriod;
    const isDefaultPage = String(localStorage.defaultFeature) === String(str);
    setIsDefault(!isDefaultPage);
    if (isDefaultPage) localStorage.defaultFeature = "guide";
    else localStorage.defaultFeature = str;
  }
  function isFeature() {
    const children = findChildren(routes, selectedFeature);
    const pass = children.some((i) => i.period !== "");
    return pass;
  }

  function changePeriod(key) {
    dispatchSelectedPeriod(key);
    setAlerts([]);

    // const str = selectedFeature + "-" + selectedPeriod;
    // const isDefaultPage = String(localStorage.defaultFeature) === String(str);
    // setIsDefault(isDefaultPage);
  }


  function setClientLogsData() {
    let loginInfo = JSON.parse(localStorage.login);
    const data = {
      unique_id: findUniqueId(routes, selectedPeriod),
      time: Math.floor(Date.now() / 1000),
      route: findParentName(routes, selectedPeriod),
      username: loginInfo?.username,
      period: routes.find((i) => String(i.id) === String(selectedPeriod))?.name || firstPeriod
    }
    saveClientLogs(data)
  }

  useEffect(() => {
    const str = selectedFeature + "-" + selectedPeriod;
    const isDefaultPage = String(localStorage.defaultFeature) === String(str);
    setIsDefault(isDefaultPage);

  }, []);

  useEffect(() => {
    setClientLogsData()
  }, [selectedPeriod]);


  useEffect(() => {
    const str = selectedFeature + "-" + selectedPeriod;
    const isDefaultPage = String(localStorage.defaultFeature) === String(str);
    setIsDefault(isDefaultPage);

  }, [selectedFeature, selectedPeriod]);

  return (
    <Col md={12}>
      <div className="breadcrumb">
        <div className="routes-info">
          <Row className="align-items-center">
            <Col md={6} sm={12}>
              <span>
                <Breadcrumb
                  items={getParentsName(routes, selectedFeature).map((name) => ({
                    title: name,
                  }))}
                />
              </span>
            </Col>
            <Col md={6} sm={12}>
              {isFeature() ? (
                <div
                  className="header-route"
                >
                  <AntdButton onClick={onChangeAlert}>Info</AntdButton>

                  <AntdButton onClick={onSetDefault}>
                    {isDefault ? "Remove Default Route" : "Set as Default Route"}
                  </AntdButton>
                  <div>
                    <span className="text">Period:</span>
                    <Dropdown
                      overlay={
                        <Periods
                          onChange={changePeriod}
                          routes={routesChildren}
                        />
                      }
                      trigger={["click"]}
                    >
                      <AntdButton>
                        <>
                          {routes.find(
                            (i) => String(i.id) === String(selectedPeriod)
                          )?.name || firstPeriod}
                          <DownOutlined />
                        </>
                      </AntdButton>
                    </Dropdown>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </Col>
          </Row>
        </div>
      </div>
    </Col>
  );
};

const HistoryChart = ({ historyChart, showHome, size }) => {
  return (
    <Col className="position-relative mb-4" xs={12} lg={size}>
      {historyChart && !showHome ? (
        <div className="chart-wrapper full-height">
          <HighchartsReact options={historyChart} highcharts={Highcharts} />
        </div>
      ) : (
        <></>
      )}
    </Col>
  );
};

const DistributionChart = ({ showHome, distributionChart, size }) => {

  const isEmpty =
    !distributionChart ||
    distributionChart.empty === true ||
    !distributionChart.series ||
    distributionChart.series.length === 0 ||
    distributionChart.series.every(s => !s.data || s.data.length === 0);


  return (
    <Col className="position-relative mb-4" xs={12} lg={size}>
      {!showHome && !isEmpty ? (
        <div className="chart-wrapper full-height">
          <HighchartsReact
            options={distributionChart}
            highcharts={Highcharts}
          />
        </div>
      ) : (
        <></>
      )}
    </Col>
  );
};

const AccuracyChart = ({ showHome, tableValue, accuracyChart, size }) => {

  return (
    <Col className="position-relative mb-4" md={12}>
      {accuracyChart && !showHome ? (
        <div className="chart-wrapper full-height">
          <HighchartsReact options={accuracyChart} highcharts={Highcharts} />
        </div>
      ) : (
        <></>
      )}
    </Col>
  );
};

function getValues(chartsData, chartName) {
  const chartData = chartsData.find((item) => item.chartName === chartName);
  const info = chartData?.value;

  return {
    title: info?.title,
    content: info?.description,
  };
}

const Charts = ({ siderController }) => {
  const [accuracyChart, setAccuracyChart] = useState(false);
  const [percentageChanges, setPercentageChanges] = useState(false);
  const [historyChart, setHistoryChart] = useState(false);
  const [distributionChart1, setDistributionChart1] = useState(false);
  const [distributionChart2, setDistributionChart2] = useState(false);
  const [distributionChart3, setDistributionChart3] = useState(false);
  const [gauge, setGauge] = useState(false);
  const [toptenCharts, setToptenCharts] = useState([]);
  const [DataStatus, setDataStatus] = useState(DataStates.Unselect);
  const [tableValue, setTableValue] = useState(false);
  const { value: chartsData } = useContext(ChartsDataContext);
  const [alerts, setAlerts] = useState([]);
  const [showAlert, setShowAlerts] = useState([]);
  const { value: selectedPeriod } = useContext(SelectedPeriodContext);
  const { value: selectedFeature } = useContext(SelectedFeatureContext);
  const { value: routes } = useContext(RoutesContext);
  const [toptenDesc, setToptenDesc] = useState("");
  const [isToptenDesc, setIsToptenDesc] = useState(false);
  const [routeName, setRouteName] = useState("");
  const hasDistributionData = (chart) =>
    chart &&
    chart.series &&
    chart.series.length > 0 &&
    chart.series.some(s => Array.isArray(s.data) && s.data.length > 0);

  useEffect(() => {
    setDataStatus(DataStates.Unselect);
    setAlerts([]);
  }, [selectedFeature]);

  useEffect(() => {
    const cancelToken = axios.CancelToken.source();
    let isCurrentRequest = true;

    setPercentageChanges(false);
    setHistoryChart(false);
    setDistributionChart1(false);
    setDistributionChart2(false);
    setDistributionChart3(false);
    setGauge(false);
    setToptenCharts([]);
    setIsToptenDesc(false);
    setToptenDesc("");
    setAccuracyChart(false);
    setDataStatus(DataStates.Unselect);
    getRoutesData(selectedPeriod, cancelToken.token)
      .then(async (res) => {
        if (!isCurrentRequest) return;

        const data = res.data;
        try {
          const lead1TopTenData = getTopTenDataForLead(data, 1);
          if (lead1TopTenData?.id) {
            let content = data?.topTenDataDesc;
            if(content && content !== ''){
              setIsToptenDesc(true);
              setToptenDesc(content);
            }else{
              setIsToptenDesc(false);
              setToptenDesc('');
            }
            // const resp = await APIManager.get(`http://localhost/sohianest/api/v2/export_data.php?action=gettoptendesc&id=${data?.topTenData?.id}`);
          }
        } catch (error) {

        }
        try {
          if (data.routeData !== null) {
            let parentAlerts = routes.find(
              (i) => String(i.id) === String(data.routeData.parent)
            ).alerts;
            parentAlerts = JSON.parse(parentAlerts) || [];
            const routeAlerts = JSON.parse(data.routeData.alerts) || [];

            const allAlerts = parentAlerts.concat(routeAlerts);

            if (Array.isArray(allAlerts)) {
              setAlerts(allAlerts);
              setShowAlerts(routeAlerts.filter((i) => i.color !== "info"));
            }
          }
        } catch (error) { }
        if (data.actualValue === null || data.predictValue === null) {
          if (Number(selectedPeriod) !== 0) {
            if (
              findChildren(routes, selectedFeature)?.some(
                (i) => i.period !== ""
              )
            ) {
              setDataStatus(DataStates.Unavailable);
            }
          }
          return;
        }
        setDataStatus(DataStates.Available);
        // setTableValue({
        //   average_accuracy: data.actualValue.average_accuracy,
        //   confidence_level: data.actualValue.confidence_level,
        // });
        // let routeName = findParentName(routes, selectedPeriod);
        const name = findParentName(routes, selectedPeriod);
        setRouteName(name);
        // if (
        //   typeof data.actualValue.value === "string" &&
        //   data.actualValue.value !== ""
        //   ) {
        //   }
        data.actualValue.value = JSON.parse(data.actualValue.value || "{}").map(
          (item, index) => {
            item.Actual_value = noise(item.Actual_value, data.routeData.id, index);
            return item;
          }
        );
        data.actualValue.value = JSON.stringify(data.actualValue.value);

        try {
          const accuracyInformation = getValues(
            chartsData,
            "user-panel-chart-accuracy"
          );
          setAccuracyChart(
            AccuracyChartDirector(data, name, accuracyInformation.title)
          );
        } catch (error) {
          console.error(error);
        }
        try {
          const changesInformation = getValues(
            chartsData,
            "user-panel-chart-changes"
          );
          setPercentageChanges(
            ChangeChartDirector(data, name, changesInformation.title)
          );
        } catch (error) {
          console.error(error);
        }
        try {
          const historyInformation = getValues(
            chartsData,
            "user-panel-chart-history"
          );
          setHistoryChart(
            HistoryChartDirector(data, name, historyInformation.title)
          );
        } catch (error) {
          console.error(error);
        }
        try {
          setDistributionChart1(
            DistributionChartDirector(data, 1, name)
          );
        } catch (error) {
          console.error(error);
        }
        try {
          setDistributionChart2(
            DistributionChartDirector(data, 2, name)
          );
        } catch (error) {
          console.error(error);
        }
        try {
          setDistributionChart3(
            DistributionChartDirector(data, 3, name)
          );
        } catch (error) {
          console.error(error);
        }
        try {
          const gaugeInformation = getValues(
            chartsData,
            "user-panel-chart-gauge"
          );
          setGauge(GaugeChartDirector(data, name, gaugeInformation.title));
        } catch (error) {
          console.error(error);
        }
        try {
          const toptenInformation = getValues(
            chartsData,
            "user-panel-chart-topten"
          );

          const charts = [1, 2, 3]
            .map((lead) => {
              const topTenData = getTopTenDataForLead(data, lead);
              if (!topTenData?.top_ten) return null;

              return {
                lead,
                chart: TopTenChartDirector(
                  topTenData,
                  name,
                  toptenInformation.title,
                  lead
                ),
              };
            })
            .filter(Boolean);

          setToptenCharts(charts);
        } catch (error) {
          console.error(error);
        }
      })
      .catch((err) => {
        if (!axios.isCancel(err) && isCurrentRequest) console.error(err);
      });

    return () => {
      isCurrentRequest = false;
      cancelToken.cancel();
    };
  }, [selectedPeriod]);


  return String(selectedFeature) === "guide" ? (
    <Guide />
  ) : (
    <Container fluid className="chart-container">
      <Row>
        <>
          <RouteHeader
            siderController={siderController}
            setAlerts={setShowAlerts}
            alerts={alerts}
          />
          {showAlert.map((item) => {
            return <Alert color={item.color}>{item.content}</Alert>;
          })}
          {DataStatus !== DataStates.Available ? (
            <NADataPage isDataAvailable={DataStatus === DataStates.Unselect} />
          ) : (
            <>
              <AccuracyChart
                size={12}
                accuracyChart={accuracyChart}
                tableValue={tableValue}
              />
              <div className="text-center" style={{marginTop:"30px"}}>
                <h4 className="mb-0">
                  Strategic Confidence Range - SCR (P10-P90)
                </h4>
                <h6 style={{color:"#666"}}>
                  {routeName}
                </h6>
              </div>
              <DistributionChart
                size={4}
                distributionChart={distributionChart1}
                />

              <DistributionChart
                size={4}
                distributionChart={distributionChart2}
              />

              <DistributionChart
                size={4}
                distributionChart={distributionChart3}
              />

              <HistoryChart size={4} historyChart={historyChart} />
              <PercentageChanges
                size={4}
                percentageChanges={percentageChanges}
              />
              <Gauge gauge={gauge} size={4} />
              <TopTenCharts charts={toptenCharts} />
              {/* Top Ten Description */}
              {isToptenDesc && toptenDesc && toptenDesc.content !== '' ? (
                <TopTenChartDescription toptenDesc={toptenDesc} />
              ) : ''}
            </>
          )}
        </>
      </Row>
    </Container>
  );
};

export default Charts;
